import {
  Home,
  Server,
  Wifi,
  Puzzle,
  ChevronDown,
  ChevronRight,
  Circle,
  AlertTriangle,
  Loader2,
} from 'lucide-react'
import { useState } from 'react'
import type {
  InfrastructureStatus,
  HAStatus,
  ProxmoxStatus,
  UnifiStatus,
  VMInfo,
} from '@/types/dashboard'

interface InfrastructurePanelProps {
  data: InfrastructureStatus | null
  loading: boolean
}

function StatusDot({ status }: { status: 'healthy' | 'degraded' | 'error' | 'disconnected' }) {
  const colors = {
    healthy: 'bg-emerald-500',
    degraded: 'bg-yellow-500',
    error: 'bg-red-500',
    disconnected: 'bg-gray-500',
  }
  return (
    <span className={`w-2 h-2 rounded-full ${colors[status]} inline-block`} />
  )
}

function CollapsibleSection({
  title,
  icon: Icon,
  status,
  children,
  defaultOpen = false,
}: {
  title: string
  icon: React.ElementType
  status: 'healthy' | 'degraded' | 'error' | 'disconnected'
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-surface-border last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 hover:bg-surface-hover transition-colors"
      >
        <Icon className="w-4 h-4 text-text-muted" />
        <span className="flex-1 text-left text-sm font-medium text-text">{title}</span>
        <StatusDot status={status} />
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-text-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-muted" />
        )}
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  )
}

function HASection({ data }: { data: HAStatus }) {
  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between text-text-muted">
        <span>Entities</span>
        <span className="text-text font-mono">{data.entityCount}</span>
      </div>
      <div className="flex justify-between text-text-muted">
        <span>Active</span>
        <span className="text-emerald-400 font-mono">{data.activeEntities}</span>
      </div>
      <div className="flex justify-between text-text-muted">
        <span>Automations</span>
        <span className="text-text font-mono">{data.automationCount}</span>
      </div>
      {data.version && (
        <div className="flex justify-between text-text-muted">
          <span>Version</span>
          <span className="text-text font-mono">{data.version}</span>
        </div>
      )}
    </div>
  )
}

function ProxmoxSection({ data }: { data: ProxmoxStatus }) {
  const runningVMs = [...data.vms, ...data.containers].filter(
    (vm) => vm.status === 'running'
  ).length
  const totalVMs = data.vms.length + data.containers.length

  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between text-text-muted">
        <span>Running</span>
        <span className="text-emerald-400 font-mono">
          {runningVMs}/{totalVMs}
        </span>
      </div>
      {data.nodeHealth && (
        <>
          <div className="flex justify-between text-text-muted">
            <span>CPU</span>
            <span className="text-text font-mono">
              {Math.round(data.nodeHealth.cpu * 100)}%
            </span>
          </div>
          <div className="flex justify-between text-text-muted">
            <span>Memory</span>
            <span className="text-text font-mono">
              {Math.round(
                (data.nodeHealth.memory.used / data.nodeHealth.memory.total) * 100
              )}
              %
            </span>
          </div>
        </>
      )}
      {/* VM List */}
      <div className="mt-2 space-y-1">
        {[...data.vms, ...data.containers].slice(0, 5).map((vm: VMInfo) => (
          <div
            key={vm.vmid}
            className="flex items-center gap-2 text-text-muted"
          >
            <Circle
              className={`w-1.5 h-1.5 ${
                vm.status === 'running'
                  ? 'text-emerald-400 fill-emerald-400'
                  : 'text-gray-500'
              }`}
            />
            <span className="truncate flex-1">{vm.name}</span>
            <span className="text-[10px] uppercase">{vm.type}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function UnifiSection({ data }: { data: UnifiStatus }) {
  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between text-text-muted">
        <span>Total Clients</span>
        <span className="text-text font-mono">{data.clientCount}</span>
      </div>
      <div className="flex justify-between text-text-muted">
        <span>Wired</span>
        <span className="text-blue-400 font-mono">{data.wiredCount}</span>
      </div>
      <div className="flex justify-between text-text-muted">
        <span>Wireless</span>
        <span className="text-violet-400 font-mono">{data.wirelessCount}</span>
      </div>
      {data.alertCount > 0 && (
        <div className="flex items-center gap-1 text-yellow-400 mt-2">
          <AlertTriangle className="w-3 h-3" />
          <span>{data.alertCount} alerts</span>
        </div>
      )}
    </div>
  )
}

function PluginsSection({
  healthy,
  total,
  degraded,
}: {
  healthy: number
  total: number
  degraded: string[]
}) {
  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between text-text-muted">
        <span>Healthy</span>
        <span className="text-emerald-400 font-mono">
          {healthy}/{total}
        </span>
      </div>
      {degraded.length > 0 && (
        <div className="mt-2">
          <div className="text-yellow-400 mb-1">Degraded:</div>
          {degraded.map((name) => (
            <div key={name} className="flex items-center gap-1 text-text-muted">
              <AlertTriangle className="w-3 h-3 text-yellow-400" />
              <span>{name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function InfrastructurePanel({
  data,
  loading,
}: InfrastructurePanelProps) {
  if (loading && !data) {
    return (
      <div className="bg-surface border border-surface-border rounded-xl p-4 h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-surface border border-surface-border rounded-xl p-4 h-full">
        <div className="text-text-muted text-sm text-center py-8">
          No infrastructure data available
        </div>
      </div>
    )
  }

  const haStatus = data.homeAssistant.connected ? 'healthy' : 'disconnected'
  const proxmoxStatus = data.proxmox.connected ? 'healthy' : 'disconnected'
  const unifiStatus = data.unifi.connected ? 'healthy' : 'disconnected'
  const pluginStatus =
    data.plugins.healthy === data.plugins.total
      ? 'healthy'
      : data.plugins.degraded.length > 0
        ? 'degraded'
        : 'healthy'

  return (
    <div className="bg-surface border border-surface-border rounded-xl overflow-hidden h-full flex flex-col">
      <div className="p-3 border-b border-surface-border">
        <h3 className="text-sm font-semibold text-text">Infrastructure</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <CollapsibleSection
          title="Home Assistant"
          icon={Home}
          status={haStatus}
          defaultOpen
        >
          <HASection data={data.homeAssistant} />
        </CollapsibleSection>

        <CollapsibleSection
          title="Proxmox"
          icon={Server}
          status={proxmoxStatus}
          defaultOpen
        >
          <ProxmoxSection data={data.proxmox} />
        </CollapsibleSection>

        <CollapsibleSection
          title="UniFi Network"
          icon={Wifi}
          status={unifiStatus}
        >
          <UnifiSection data={data.unifi} />
        </CollapsibleSection>

        <CollapsibleSection
          title="Plugins"
          icon={Puzzle}
          status={pluginStatus}
        >
          <PluginsSection
            healthy={data.plugins.healthy}
            total={data.plugins.total}
            degraded={data.plugins.degraded}
          />
        </CollapsibleSection>
      </div>
    </div>
  )
}
